import React, { ReactNode, useEffect, useRef, useState } from "react";
import { Icon, Tooltip, Typography } from "top-brains-ui";
import styles from "@src/components/expandable-drawer/ExpandableDrawer.module.scss";
// import CommonFilter from "../common-filter/CommonFilter";
import {
  COMMON_COLORS,
  ICON_COLOR_CONSTANTS,
} from "@src/styles/ColorConstants";
import Button from "../button/Button";
import ReactDOM from "react-dom";
import classNames from "classnames";

interface IconProps {
  name: string;
  onClick: () => void;
  height?: number;
  width?: number;
  className?: string;
  color?: string;
  hoverEffect?: boolean;
  disabled?: boolean;
  isButton?: boolean;
  label?: string;
  variant?: "primary" | "secondary" | "danger";
}

interface ExpandableDrawerProps {
  headerTitle?: string | ReactNode;
  showFullScreenToggle?: boolean;
  showBackIcon?: boolean;
  fullScreenHeight?: string;
  children: ReactNode;
  footerLeftContent?: ReactNode;
  footerRightContent?: ReactNode;
  buttons?: {
    label: string;
    variant?: "primary" | "secondary" | "danger" | string;
    onClick: () => void;
    disable?: true | false;
    type?: "button" | "submit";
    tooltip?: string;
  }[];
  headerIcons?: (IconProps | ReactNode)[];
  width?: string;
  height?: string;
  showOverlay?: boolean;
  isFooterRequired?: boolean;
  size?: "small" | "medium" | "large" | "xLarge";
  onClose?: () => void;
  showCloseIcon?: boolean;
  isOpen?: boolean;
  type?: "screen" | "normal";
  direction?: "right" | "left";
  maxWidth?: string;
  zIndex?: number;
  marginRight?: string;
  marginLeft?: string;
  variant?: "website" | "default";
  headerBGColor?: string;
  headerLeftContent?: ReactNode;
  positionTop?: string;
  positionBottom?: string;
  headerRightContent?: ReactNode;
  headerClassName?: string;
  showFullScreenIconToStart?: boolean;
}

const ChildDrawer: React.FC<
  ExpandableDrawerProps & { onExited: () => void }
> = ({
  headerTitle = "Default Header",
  showFullScreenToggle = false,
  showBackIcon = false,
  // fullScreenHeight = "85dvh",
  children,
  isFooterRequired = true,
  footerLeftContent = null,
  // footerRightContent = null,
  buttons = [],
  headerIcons = [],
  width,
  height = "90dvh",
  showOverlay = false,
  size,
  onClose,
  showCloseIcon = false,
  isOpen = true,
  type = "normal",
  direction = "right",
  maxWidth,
  zIndex = 998,
  onExited,
  marginLeft = "5px",
  marginRight = "5px",
  variant = "default",
  headerBGColor = "",
  headerLeftContent = <></>,
  positionTop,
  positionBottom,
  headerRightContent = <></>,
  headerClassName,
  showFullScreenIconToStart = false,
}) => {
  const sizeMap: { [key: string]: string } = {
    small: "444px",
    medium: "666px",
    large: "888px",
    xLarge: "1222px",
  };

  const [isFullScreen, setIsFullScreen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const filterRef = useRef<HTMLButtonElement>(null);
  // const [filterModalOpen, setFilterModalOpen] = useState(false);

  useEffect(() => {
    // Delay setting visible so transition can be seen
    const timeout = setTimeout(() => setIsVisible(true), 8);
    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    // If isOpen becomes false, start transition out
    if (!isOpen) {
      setIsVisible(false);
      const timeout = setTimeout(() => onExited(), 800); // Match transition duration
      return () => clearTimeout(timeout);
    }
  }, [isOpen, onExited]);

  // const handleCloseModal = () => {
  //   setFilterModalOpen(prev => !prev);
  // };

  const toggleFullScreen = () => {
    setIsFullScreen(prev => !prev);
  };

  return (
    <>
      {/* {filterModalOpen ? (
        <CommonFilter
          onClose={handleCloseModal}
          ref={filterRef}
          onProceed={data => {
            console.log("data", data);
          }}
          filtersType={{
            quesType: true,
            quesStatus: true,
            difficulty: true,
            approvedBy: true,
          }}
          defaultFilter={{
            quesStatus: { label: "New", id: "10" },
            difficulty: { label: "Easy", id: "12" },
          }}
        />
      ) : null} */}
      {ReactDOM.createPortal(
        <>
          <div
            className={`${styles.overlay} ${
              isOpen && showOverlay ? styles.visible : ""
            }`}
            style={{ zIndex: zIndex }}
          />

          <div
            className={styles.expandableDrawer}
            style={{
              minWidth: isFullScreen
                ? "99%"
                : width || (size !== undefined ? sizeMap[size] : "70%"),
              width: isFullScreen ? "99%" : width || "70%",
              maxWidth: isFullScreen
                ? "99%"
                : maxWidth
                  ? maxWidth
                  : width || (size !== undefined ? sizeMap[size] : "70%"),
              position: "fixed",
              top: positionTop
                ? positionTop
                : type === "normal"
                  ? "54px"
                  : "85px",
              bottom: positionBottom ? positionBottom : "5px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              right: direction === "right" ? marginRight : "",
              left: direction === "left" ? marginLeft : "",
              backgroundColor: variant === "website" ? "var(--main-table-bg-color, #0F120A)" : "white",
              transition: "all 0.8s ease-in-out",
              zIndex: zIndex,
              borderTopRightRadius: "8px",
              borderBottomRightRadius: "8px",
              borderTopLeftRadius:
                !isFullScreen && type === "screen" ? "0px" : "8px",
              borderBottomLeftRadius:
                !isFullScreen && type === "screen" ? "0px" : "8px",
              transform:
                direction === "right"
                  ? isVisible
                    ? "translateX(0)"
                    : "translateX(102%)"
                  : isVisible
                    ? "translateX(0)"
                    : "translateX(-102%)",
              border: variant === "website" ? "none" : "",
            }}
          >
            <div
              className={`${styles.expandableDrawerHeader} ${headerClassName}`}
              style={{
                backgroundColor: headerBGColor,
                borderTopLeftRadius: isFullScreen ? "8px" : "0px",
                color: variant === "website" ? "var(--website-text-color1, #fff)" : "",
              }}
            >
              <div className={styles.expandableDrawerLeftContent}>
                {showFullScreenIconToStart && showFullScreenToggle && (
                  <Icon
                    name="drawer_maximize"
                    onClick={toggleFullScreen}
                    className={classNames(styles.maximizeIcon, {
                      [styles.rotate]: isFullScreen,
                    })}
                  />
                )}
                {headerLeftContent}
                {showFullScreenToggle && !showFullScreenIconToStart && (
                  <Icon
                    name="drawer_maximize"
                    onClick={toggleFullScreen}
                    className={classNames(styles.maximizeIcon, {
                      [styles.rotate]: isFullScreen,
                    })}
                  />
                )}
                {showBackIcon && (
                  <Icon
                    name="back_icon"
                    onClick={onClose}
                    height={8}
                    width={8}
                    className={styles.backIconStyle}
                    color={ICON_COLOR_CONSTANTS.EDIT_COLOR_ICON}
                  />
                )}
                <Typography
                  as="div"
                  fontWeight="semi-bold"
                  fontSize={variant === "website" ? 14 : 12}
                  color={variant === "website" ? "var(--website-text-color1, #fff)" : ""}
                >
                  {headerTitle}
                </Typography>
              </div>
              <div className={styles.expandableDrawerRightContent}>
                {headerRightContent}
                <div className={styles.headerIcons}>
                  {headerIcons?.map((icon, index) => {
                    if (React.isValidElement(icon)) {
                      return <div key={`icon-${index}`}>{icon}</div>;
                    }

                    if (icon && typeof icon === "object" && "name" in icon) {
                      return (
                        <button
                          key={`button-${index}`}
                          className="regular-icon filter-icon"
                          ref={filterRef}
                          onClick={() => {
                            if (icon.name === "filter") {
                              // setFilterModalOpen(true);
                            } else {
                              icon.onClick();
                            }
                          }}
                        >
                          <Icon
                            name={icon.name}
                            color={icon.color}
                            height={icon.height || 12}
                            width={icon.width || 12}
                            className={icon.className}
                          />
                        </button>
                      );
                    }

                    return null;
                  })}
                  {showCloseIcon && (
                    <Icon
                      name="close"
                      onClick={onClose}
                      className={`${
                        variant === "website"
                          ? styles.websiteCloseIconStyle
                          : styles.closeStyles
                      }`}
                      height={variant === "website" ? 12 : 12}
                      width={variant === "website" ? 12 : 12}
                      color={
                        variant === "website"
                          ? "var(--website-text-color1, #D4F39A)"
                          : ICON_COLOR_CONSTANTS.FILTER_COLOR_ICON
                      }
                    />
                  )}
                </div>
              </div>
            </div>
            <div
              className={styles.expandableDrawerBody}
              style={{
                height: isFooterRequired ? `calc(100% - 68px)` : height,
              }}
            >
              {children}
            </div>
            {isFooterRequired && (footerLeftContent || buttons.length > 0) && (
              <div className={styles.expandableDrawerFooterContent}>
                {footerLeftContent && (
                  <div className={styles.footerLeftContent}>
                    {footerLeftContent}
                  </div>
                )}
                <div className={styles.footerRightContent}>
                  {buttons.length > 0 && (
                    <div className={styles.footerButtons}>
                      {buttons.map((button, index) => (
                        <Tooltip
                          title={button.tooltip}
                          key={`button-tooltip-${index}`}
                          placement="top"
                        >
                          <Button
                            key={`button-${index}`}
                            variant={button.variant || "primary"}
                            label={button.label}
                            onClick={button.onClick}
                            disabled={button.disable}
                          />
                        </Tooltip>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </>,
        document.body,
      )}
    </>
  );
};

const ExpandableDrawer: React.FC<ExpandableDrawerProps> = props => {
  const [shouldRender, setShouldRender] = useState(props.isOpen || false);

  useEffect(() => {
    if (props.isOpen) {
      setShouldRender(true);
    }
  }, [props.isOpen]);

  const handleExited = () => {
    setShouldRender(false);
  };

  return shouldRender ? (
    <ChildDrawer {...props} onExited={handleExited} />
  ) : null;
};

export default ExpandableDrawer;
